import { Injectable } from '@angular/core';
import {
  CanActivate,
  CanActivateChild,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  Router,
} from '@angular/router';
import { AuthService } from '../services/auth.service';
import { UserRole } from '../enums/role.enum';
import { SessionService } from '../services/session-service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard implements CanActivate, CanActivateChild {

  constructor(
    private auth: AuthService,
    private router: Router,
    private sessionService: SessionService
  ) { }

  // Guard for main routes
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    return this.checkAccess(route, state);
  }

  // Guard for child routes
  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    return this.checkAccess(childRoute, state);
  }

  // Shared access check logic
  private checkAccess(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    const token = this.auth.getToken();
    const publicToken = route.queryParamMap.get('token');

    // Allow public access if route supports token access
    if (route.data['allowPublicToken'] && publicToken) {
      return true;
    }

    // Resolve explicitly public routes (login / MFA) before any token checks so a
    // missing/expired token never blocks reaching them.
    const routeConfigPath = route.routeConfig?.path || '';
    const normalizedUrl = state.url.split('?')[0].replace(/\/+$|\/\/$/g, '');
    const isLoginRoute = routeConfigPath === 'login' || routeConfigPath === 'admin/login' || normalizedUrl === '/login' || normalizedUrl === '/admin/login';
    const isMfaRoute = routeConfigPath === 'mfa-setup' || routeConfigPath === 'mfa-verification';

    if (isLoginRoute || isMfaRoute || route.data['allowPublicToken']) {
      return true;
    }

    if (!token) {
      // User is simply logged out → redirect silently
      const loginRoute = this.auth.isAdminUser() ? '/admin/login' : '/login';
      this.router.navigate([loginRoute]);
      return false;
    }
    if (this.auth.isTokenExpired(token)) {
      // Token existed but expired → show modal
      this.sessionService.notifySessionExpired();
      return false;
    }

    // Check admin/tenant route separation using the actual URL
    const isAdminUser = this.auth.isAdminUser();

    // Prevent admin users from accessing tenant routes and vice versa with strict URL checks
    const isAdminUrl = normalizedUrl.startsWith('/admin');
    const isTenantUrl = !isAdminUrl;

    if (isAdminUser && isTenantUrl && normalizedUrl !== '' && normalizedUrl !== '/') {
      this.router.navigate(['/admin/dashboard']);
      return false;
    }

    if (!isAdminUser && isAdminUrl) {
      this.router.navigate(['/dashboard']);
      return false;
    }

    // If no allowedRoles specified in route, allow access
    const allowedRoles = route.data['allowedRoles'] as UserRole[];
    if (!allowedRoles || allowedRoles.length === 0) {
      return true;
    }

    // Get actual user role from AuthService
    const userRole = this.auth.getUserRole();

    if (allowedRoles.includes(userRole)) {
      return true;
    }

    // Redirect to appropriate login based on user type
    const loginRoute = isAdminUser ? '/admin/login' : '/login';
    this.router.navigate([loginRoute]);
    return false;
  }
}
